import { useEffect } from "react";
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from "@azure/msal-react";
import { loginRequest } from "@/config/authConfig";
import { useAuth } from "@/hooks/useAuth";
import { useUserApi } from "@/hooks/apiHooks/useUserApi";

export const ProtectedRoute = ({
    children,
    redirectUri,
}: ProtectedRouteProps) => {
    const { instance, isAuthenticated, isInitialized, user } = useAuth();
    const { getMeUser } = useUserApi();

    useEffect(() => {
        const initAuth = async () => {
            if (!isInitialized) {
                return;
            }

            if (!isAuthenticated) {
                instance.loginRedirect({
                    ...loginRequest,
                    redirectUri: redirectUri,
                });
                return;
            }

            // useAccount can resolve a render later than accounts.length > 0,
            // so wait for user to be non-null before calling APIs that require
            // an access token.
            if (!user) {
                return;
            }

            await getMeUser();
        };

        // Caught, not floating: getMeUser drives userFetchErrorAtom and the
        // gates render that failed state — a bare rejection here would be an
        // unhandled one beside it.
        initAuth().catch(() => {
            /* the error atom drives the UI */
        });
    }, [
        instance,
        isAuthenticated,
        isInitialized,
        redirectUri,
        getMeUser,
        user,
    ]);

    return (
        <>
            <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <div>Redirecting to login...</div>
            </UnauthenticatedTemplate>
        </>
    );
};

interface ProtectedRouteProps {
    children: React.ReactNode;
    redirectUri: string;
}
